import React, {
  FunctionComponent,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useState,
  useMemo
} from 'react';
import styles from './contact-form.module.scss';
import axios from 'axios';
import Loader from 'react-loader-spinner';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '../../button/button';
import { IInputProps, Input } from '../components/input/input';
import { useGlobalTranslations } from '../../../hooks/use-global-translations';
import { AppActionType, AppStore } from '../../../store/app.context';
import {
  IFormComponentProps,
  ContactFormAction,
  ContactFormActionType,
  ContactFormState,
  Field,
  GroupField,
  Error
} from './contact-form-types';
import { Checkbox, ICheckboxProps } from '../components/checkbox/checkbox';
import { GroupedCheckboxes } from '../components/groupedCheckboxes/groupedCheckboxes';
import { GroupedRadioButtons } from '../components/groupedRadioButtons/groupedRadioButtons';
import Cookies from 'js-cookie';

const initialState: ContactFormState = {
  checkIsValid: false,
  isError: false,
  formSend: false,
  formFields: undefined,
  groupedCheckboxesFields: undefined,
  groupedCheckboxesErrors: undefined,
  isTouched: false
};

export const ContactForm: FunctionComponent<IFormComponentProps> = ({
  contactButtonCta,
  formClassName,
  inputs,
  checkboxes,
  groupedCheckboxes,
  contactPerson,
  postUrl,
  privacyPolicyAgreement,
  successMessage,
  userCountry,
  clientIp,
  campaignId,
  webinarTag,
  useReCAPTCHA
}) => {
  const {
    dispatch,
    state: {
      isLoading,
      showPageState: { origin, currentUrl, prevUrl }
    }
  } = useContext(AppStore);
  const [
    {
      checkIsValid,
      isError,
      formFields,
      groupedCheckboxesFields,
      groupedCheckboxesErrors,
      formSend,
      isTouched
    },
    contactFormDispatch
  ] = useReducer(reducer, initialState);
  const [showReCAPTCHA, setShowReCAPTCHA] = useState(false);
  const [isErrorReCAPTCHA, setIsErrorReCAPTCHA] = useState(false);
  const {
    requestErrorText,
    mailSendSuccessMessage,
    sendingMail,
    reCAPTCHAErrorMessage
  } = useGlobalTranslations();
  const [initialFieldValues, setInitialFieldValues] = useState<
    Field | undefined
  >(undefined);
  const [initialGroupedFieldValues, setInitialGroupedFieldValues] = useState<
    GroupField | undefined
  >(undefined);

  const captchaRef = React.useRef(null);

  useEffect(() => {
    const inputFields = inputs.reduce((value, currValue): Field => {
      return {
        ...value,
        [currValue.name]: ''
      };
    }, {});
    const checkboxFields = checkboxes.reduce((value, currValue): Field => {
      return {
        ...value,
        [currValue.name]: false
      };
    }, {});
    const radioButtonsFields = groupedCheckboxes?.reduce((value, currValue) => {
      if (currValue.multipleChoiceCheckboxes) {
        return {
          ...value
        };
      }

      return {
        ...value,
        [currValue.groupName]: ''
      };
    }, {});
    const groupedCheckboxesFields = groupedCheckboxes?.reduce(
      (value, currValue) => {
        if (currValue.multipleChoiceCheckboxes) {
          return {
            ...value,
            [currValue.groupName]: []
          };
        }

        return {
          ...value
        };
      },
      {}
    );

    setInitialFieldValues({
      ...inputFields,
      ...checkboxFields,
      ...radioButtonsFields
    });

    if (groupedCheckboxesFields) {
      setInitialGroupedFieldValues(groupedCheckboxesFields);
    }
    contactFormDispatch({
      type: ContactFormActionType.SET_FIELDS,
      payload: {
        fields: { ...inputFields, ...checkboxFields, ...radioButtonsFields },
        groupedCheckboxesFields: { ...groupedCheckboxesFields }
      }
    });
    contactFormDispatch({
      type: ContactFormActionType.SET_GROUPED_ERRORS,
      payload: {
        errors: { ...radioButtonsFields, ...groupedCheckboxesFields }
      }
    });
  }, [inputs, checkboxes, groupedCheckboxes]);

  useEffect(() => {
    if (useReCAPTCHA) checkReCAPTCHA();
  }, [clientIp]);

  const checkGroupsValidity = (): boolean =>
    Boolean(
      groupedCheckboxesErrors &&
        Object.values(groupedCheckboxesErrors).every(value => !value)
    );

  const checkReCAPTCHA = async () => {
    try {
      const res = await axios.post(
        process.env.SECURITY_APP ||
          'https://swh-p-spambotprot-fap.azurewebsites.net/api/SpamBotProtection',
        {
          clientIP: clientIp,
          action: 'check'
        }
      );
      if (res.status === 202) setShowReCAPTCHA(true);
      if (res.status === 200) setShowReCAPTCHA(false);
    } catch (e) {
      console.log(e);
    }
  };

  const checkReCAPTCHAField = () => {
    if (captchaRef.current.getValue()) {
      setIsErrorReCAPTCHA(false);
      return true;
    } else {
      setIsErrorReCAPTCHA(true);
      return false;
    }
  };

  const setError = useCallback(
    (errors: Error): void =>
      contactFormDispatch({
        type: ContactFormActionType.UPDATE_GROUPED_ERRORS,
        payload: { errors }
      }),
    []
  );

  const submitHandler = async (event: React.FormEvent): Promise<void> => {
    const target = event.target as HTMLFormElement;

    event.preventDefault();
    contactFormDispatch({ type: ContactFormActionType.REMOVE_MESSAGE });
    contactFormDispatch({ type: ContactFormActionType.CHECK_VALIDITY });

    const reCAPTCHA =
      showReCAPTCHA && useReCAPTCHA ? checkReCAPTCHAField() : true;

    if (target.checkValidity() && checkGroupsValidity() && reCAPTCHA) {
      dispatch({ type: AppActionType.loading });

      try {
        if (useReCAPTCHA) {
          if (showReCAPTCHA) {
            const token = captchaRef.current.getValue();
            if (token) {
              try {
                const res = await axios.post(
                  process.env.SECURITY_APP ||
                    'https://swh-p-spambotprot-fap.azurewebsites.net/api/SpamBotProtection',
                  {
                    clientIP: clientIp,
                    action: 'submit',
                    captchaUrl: `https://www.google.com/recaptcha/api/siteverify?secret=${process.env.RECAPTCHA_SECRET_KEY}&response=${token}`
                  }
                );
                if (res.status === 202) setShowReCAPTCHA(true);
                if (res.status === 200) setShowReCAPTCHA(false);
              } catch {
                contactFormDispatch({ type: ContactFormActionType.ERROR });
                dispatch({ type: AppActionType.stopLoading });
                return;
              }
            } else {
              contactFormDispatch({ type: ContactFormActionType.ERROR });
              dispatch({ type: AppActionType.stopLoading });
              return;
            }
          } else {
            const res = await axios.post(
              process.env.SECURITY_APP ||
                'https://swh-p-spambotprot-fap.azurewebsites.net/api/SpamBotProtection',
              {
                clientIP: clientIp,
                action: 'submit'
              }
            );
            if (res.status === 202) setShowReCAPTCHA(true);
            if (res.status === 200) setShowReCAPTCHA(false);
          }
        }

        if (campaignId && webinarTag) {
          await axios.post(process.env.GET_RESPONSE_GATEWAY, {
            bussinesEmail: formFields?.bussinesEmail,
            fullName: formFields?.fullName,
            campaignId: campaignId,
            tagName: webinarTag
          });
        } else {
          await axios.post(postUrl ? postUrl : process.env.CONTACT_FORM, {
            source: document.referrer ? document.referrer : 'No source',
            refferer: prevUrl ? `${origin}${prevUrl}` : 'No prev URL',
            currentUrl,
            contactEmail: contactPerson?.email,
            formFields: { ...formFields, ...groupedCheckboxesFields }
          });

          if (formFields) {
            const {
              fullName,
              bussinesEmail,
              phoneNumber,
              whereDidYouFindUs
            } = formFields;

            const data = {
              name: contactPerson?.firstName + ' ' + contactPerson?.lastName,
              content: {
                ...formFields,
                ...groupedCheckboxesFields,
                source: document.referrer ? document.referrer : 'No source',
                refferer: prevUrl ? `${origin}${prevUrl}` : 'No prev URL',
                currentUrl,
              },
              person: {
                name: fullName,
                email: bussinesEmail,
                phone: phoneNumber,
                country: userCountry,
                whereDidYouFindUs
              },
              marketingAgreement: formFields.marketingAgreement || false,
              inbound: Cookies.get('inbound') || 'Inbound - Unidentified',
              businessUnit: 'SoftwareHut'
            };

            axios.post(process.env.PIPDRIVE_URL, data);

            if (formFields?.marketingAgreement) {
              await axios.post(process.env.GET_RESPONSE_GATEWAY, {
                bussinesEmail: formFields.bussinesEmail,
                fullName: formFields.fullName,
                campaignId: 'AI',
                tagName: process.env.GR_TAG_NAME
              });
            }
          }
        }
        dispatch({ type: AppActionType.stopLoading });
        contactFormDispatch({ type: ContactFormActionType.SUCCESS_MESSAGE });
        contactFormDispatch({
          type: ContactFormActionType.RESET_FORM,
          payload: { initialFieldValues, initialGroupedFieldValues }
        });
      } catch (error) {
        contactFormDispatch({ type: ContactFormActionType.ERROR });
        dispatch({ type: AppActionType.stopLoading });
      }
    }
  };

  const groupedCheckboxesFieldsHandler = useCallback(
    (groupName: string, groupedFieldValue: string): void => {
      contactFormDispatch({ type: ContactFormActionType.SET_IS_TOUCHED });
      contactFormDispatch({
        type: ContactFormActionType.GROUP_VALUE_CHANGE,
        payload: { groupedFieldValue, groupName }
      });

      if (checkIsValid) {
        contactFormDispatch({ type: ContactFormActionType.REMOVE_MESSAGE });
      }
    },
    [checkIsValid]
  );

  const formFieldValueHandler = useCallback(
    (event: React.SyntheticEvent, groupName?: string): void => {
      let fieldValue;
      const target = event.target as HTMLInputElement;
      const fieldName = target.name;
      const fieldType = target.type;
      fieldType === 'checkbox'
        ? (fieldValue = target.checked)
        : (fieldValue = target.value);

      contactFormDispatch({ type: ContactFormActionType.SET_IS_TOUCHED });
      contactFormDispatch({
        type: ContactFormActionType.FORM_VALUE_CHANGE,
        payload: { fieldName: groupName || fieldName, fieldValue }
      });

      if (checkIsValid) {
        contactFormDispatch({ type: ContactFormActionType.REMOVE_MESSAGE });
      }
    },
    [checkIsValid]
  );

  const requestMessageText = useMemo(() => {
    if (isError) {
      return requestErrorText;
    }
    return successMessage ? successMessage : mailSendSuccessMessage;
  }, [isError, mailSendSuccessMessage, requestErrorText, successMessage]);

  return (
    <form
      onSubmit={submitHandler}
      noValidate
      className={`
        ${styles.form} 
        ${checkIsValid ? styles.formSubmitted : null}
        ${formClassName ? formClassName : 'GetInTouchForm'}`}
    >
      <div
        className={styles.privacyPolicyText}
        dangerouslySetInnerHTML={{ __html: privacyPolicyAgreement }}
      />
      {inputs &&
        inputs.map((item: IInputProps, i) => (
          <Input
            key={i}
            value={
              formFields && item.name ? (formFields[item.name] as string) : ''
            }
            type={item.type}
            onChange={formFieldValueHandler}
            label={item.label}
            name={item.name}
            errorMessage={item.errorMessage}
            required={item.required}
          />
        ))}
      {groupedCheckboxes &&
        groupedCheckboxes.map(checkboxesGroup => {
          if (checkboxesGroup.multipleChoiceCheckboxes) {
            return (
              <GroupedCheckboxes
                key={checkboxesGroup.groupName}
                {...checkboxesGroup}
                checkIsValid={checkIsValid}
                onChange={groupedCheckboxesFieldsHandler}
                formFields={groupedCheckboxesFields}
                setError={setError}
              />
            );
          }

          return (
            <GroupedRadioButtons
              key={checkboxesGroup.groupName}
              {...checkboxesGroup}
              checkIsValid={checkIsValid}
              onChange={formFieldValueHandler}
              formFields={formFields}
              setError={setError}
            />
          );
        })}
      {checkboxes &&
        checkboxes.map((item: ICheckboxProps, i) => (
          <Checkbox
            key={i}
            value={
              formFields && item.name
                ? (formFields[item.name] as boolean)
                : false
            }
            onChange={formFieldValueHandler}
            name={item.name}
            label={item.label}
            className={`${styles.ndaCheckbox} ${formFields &&
              item.name &&
              formFields[item.name] === true &&
              styles.active}`}
            checkIsValid={checkIsValid}
            required={item.required}
            formIsTouched={isTouched}
            displayOnInput={item.displayOnInput}
            smallText={item.smallText}
          />
        ))}
      {useReCAPTCHA && (
        <div>
          {showReCAPTCHA && (
            <>
              <ReCAPTCHA
                sitekey={process.env.RECAPTCHA_SITE_KEY}
                ref={captchaRef}
                onChange={checkReCAPTCHAField}
              />
              {isErrorReCAPTCHA && (
                <p className={styles.reCaptchaErrorMessage}>
                  {reCAPTCHAErrorMessage}
                </p>
              )}
            </>
          )}
        </div>
      )}
      <div
        className={`${styles.errorMessage} ${formSend &&
          styles.successMessage} ${(isError || formSend) &&
          styles.messageVisible}`}
      >
        {requestMessageText}
      </div>
      <div className={styles.submitBtn}>
        {!isLoading ? (
          <Button
            border="rectangular"
            size="large"
            CTA={true}
            text={contactButtonCta}
            internalLink={null}
            theme="transparent"
            type="submit"
          />
        ) : (
          <div className={styles.Loader}>
            <Loader type="Oval" color="#e64510" height={50} width={50} />
            <p>{sendingMail}</p>
          </div>
        )}
      </div>
    </form>
  );
};

function reducer(
  state: ContactFormState,
  { type, payload }: ContactFormAction
): ContactFormState {
  switch (type) {
    case ContactFormActionType.CHECK_VALIDITY:
      return {
        ...state,
        checkIsValid: true
      };
    case ContactFormActionType.SET_IS_TOUCHED:
      return {
        ...state,
        isTouched: true
      };
    case ContactFormActionType.ERROR:
      return {
        ...state,
        isError: true
      };
    case ContactFormActionType.SUCCESS_MESSAGE:
      return {
        ...state,
        formSend: true
      };
    case ContactFormActionType.REMOVE_MESSAGE:
      return {
        ...state,
        isError: false,
        formSend: false
      };
    case ContactFormActionType.RESET_FORM:
      if (!payload) {
        return initialState;
      }

      return {
        ...initialState,
        checkIsValid: false,
        formSend: true,
        formFields: {
          ...payload.initialFieldValues
        },
        groupedCheckboxesFields: payload.initialGroupedFieldValues
      };
    case ContactFormActionType.SET_FIELDS:
      return {
        ...state,
        formFields: payload?.fields,
        groupedCheckboxesFields: payload?.groupedCheckboxesFields
      };
    case ContactFormActionType.FORM_VALUE_CHANGE:
      if (!payload?.fieldName) {
        return state;
      }

      return {
        ...state,
        formFields: {
          ...state.formFields,
          [payload.fieldName]: payload.fieldValue || ''
        }
      };
    case ContactFormActionType.SET_GROUPED_ERRORS:
      return {
        ...state,
        groupedCheckboxesErrors: payload?.errors
      };
    case ContactFormActionType.UPDATE_GROUPED_ERRORS:
      if (!payload?.errors) {
        return state;
      }

      return {
        ...state,
        groupedCheckboxesErrors: {
          ...state.groupedCheckboxesErrors,
          ...payload.errors
        }
      };
    case ContactFormActionType.GROUP_VALUE_CHANGE:
      if (!payload?.groupedFieldValue || !payload?.groupName) {
        return state;
      }

      return {
        ...state,
        groupedCheckboxesFields: {
          ...state.groupedCheckboxesFields,
          [payload.groupName]:
            state.groupedCheckboxesFields &&
            state.groupedCheckboxesFields[payload.groupName].includes(
              payload.groupedFieldValue
            )
              ? state.groupedCheckboxesFields[payload.groupName].filter(
                  item => item !== payload.groupedFieldValue
                )
              : state.groupedCheckboxesFields
              ? [
                  ...state.groupedCheckboxesFields[payload.groupName],
                  payload.groupedFieldValue
                ]
              : []
        }
      };

    default:
      return state;
  }
}
